'use client'

import { getTokenImageUrl } from '@/domain/euler'
import {
  isParallelIncentivizedBorrowPair,
  useParallelBorrowIncentives,
} from '@/domain/hooks/useParallelBorrowIncentives'
import { usePointsData } from '@/domain/points/pointsData/usePointsData'
import { useCardSolverConfigs, useStoreHydrated } from '@/domain/state'
import { CardRouteBadge } from '@/features/card/components/CardRouteBadge'
import {
  getEulerBorrowCardRouteBadge,
  getPooledBorrowCardRouteBadge,
} from '@/features/card/utils/cardSolverRouteMatching'
import { formatMarketsDisplay } from '@/features/evault-details/data/products'
import { assets } from '@/ui/assets'
import { Dash } from '@/ui/atoms/dash/Dash'
import { TokenImage } from '@/ui/atoms/token-image/TokenImage'
import { Tooltip, TooltipContentShort, TooltipTrigger } from '@/ui/atoms/tooltip/Tooltip'
import { cn } from '@/ui/utils/style'
import { ArrowRight } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useMemo } from 'react'
import { useAccount } from 'wagmi'
import { getPairPointsInfo } from '../logic/getPairPointsInfo'
import type { BorrowPair } from '../logic/useBorrowPairs'
import { BORROW_VAULT_CHAIN_ID } from '../logic/useVaultList'
import { ParallelIncentiveBadge } from './ParallelIncentiveBadge'

const DEFAULT_CURATOR_LOGO = '/hypurr-paw.svg'

interface ExtendedBorrowPair extends BorrowPair {
  routeType?: 'euler' | 'pooled'
  clusterName?: string
  walletBalanceAmount?: number
}

interface EBorrowListViewProps {
  pairs: ExtendedBorrowPair[]
  showLeverageMetrics?: boolean
}

function formatLiquidityDisplay(liquidityUSD: number | undefined): string {
  if (liquidityUSD === undefined) return '-'
  if (liquidityUSD >= 1_000_000) return `$${(liquidityUSD / 1_000_000).toFixed(1)}M`
  if (liquidityUSD >= 1_000) return `$${(liquidityUSD / 1_000).toFixed(0)}K`
  return `$${liquidityUSD.toFixed(0)}`
}

function BorrowCard({
  pair,
  showLeverageMetrics,
}: {
  pair: ExtendedBorrowPair
  showLeverageMetrics: boolean
}) {
  const { address } = useAccount()
  const { getByAddress } = usePointsData()
  const { byAddress: cardSolverConfigsByAddress } = useCardSolverConfigs()
  const isStoreHydrated = useStoreHydrated()
  const parallelBorrowIncentives = useParallelBorrowIncentives()
  const cardSolverConfigs = useMemo(
    () => (isStoreHydrated && address ? Object.values(cardSolverConfigsByAddress[address.toLowerCase()] ?? {}) : []),
    [address, cardSolverConfigsByAddress, isStoreHydrated],
  )

  const collateralImage = getTokenImageUrl(pair.collateralTokenInfo, pair.collateralSymbol || '')
  const liabilityImage = getTokenImageUrl(pair.liabilityTokenInfo, pair.liabilitySymbol || '')
  const marketDisplay = formatMarketsDisplay(pair.collateralMarket, pair.liabilityMarket)
  const curatorName = pair.collateralCuratorName || pair.liabilityCuratorName || 'HypurrFi'
  const displayName = pair.clusterName ?? marketDisplay?.text ?? curatorName

  const maxLTVPercent =
    pair.maxLTV !== undefined && typeof pair.maxLTV === 'bigint' && pair.maxLTV > 0n
      ? `${(Number(pair.maxLTV) / 100).toFixed(0)}%`
      : null
  const liqLTVPercent =
    pair.liquidationLTV !== undefined && typeof pair.liquidationLTV === 'bigint' && pair.liquidationLTV > 0n
      ? `${(Number(pair.liquidationLTV) / 100).toFixed(0)}%`
      : null

  const liquidityDisplay = formatLiquidityDisplay(pair.liquidityUSD)
  const hasPosition = (pair.userBorrowAmount ?? 0) > 0

  const { hasHypurrPoints, partnerPoints: borrowContextPoints } = getPairPointsInfo(pair, getByAddress)
  const hasParallelIncentive =
    isParallelIncentivizedBorrowPair(pair.collateral, pair.liability) && !!parallelBorrowIncentives
  const hasPoints = hasHypurrPoints || borrowContextPoints.length > 0

  const maxRoe = showLeverageMetrics ? pair.maxRoe : undefined
  const isPositiveRoe = (maxRoe ?? 0) >= 0
  const cardRouteBadge =
    pair.routeType === 'euler'
      ? getEulerBorrowCardRouteBadge(cardSolverConfigs, {
          borrowVaultAddress: pair.liability,
          subAccountId: pair.firstSubAccountWithPosition,
        })
      : pair.routeType === 'pooled'
        ? getPooledBorrowCardRouteBadge(cardSolverConfigs, pair.liability)
        : null

  let destination: string
  if (pair.routeType === 'pooled') {
    destination = `/markets/pooled/${BORROW_VAULT_CHAIN_ID}/${pair.liability}`
  } else {
    const params = new URLSearchParams()
    if (pair.firstSubAccountWithPosition !== undefined) {
      params.set('subAccountId', String(pair.firstSubAccountWithPosition))
    }
    if (showLeverageMetrics) {
      params.set('multiply', 'true')
    }
    const queryString = params.toString()
    destination = `/markets/eborrow/${pair.collateral}/${pair.liability}${queryString ? `?${queryString}` : ''}`
  }

  const pointsIcons = hasPoints ? (
    <div className="flex items-center">
      {hasHypurrPoints && (
        <div className="relative h-4 w-4 rounded-full border border-background" style={{ zIndex: 10 }}>
          <Image src={assets.hypurrPawNoShadow} alt="HP" className="h-full w-full" />
        </div>
      )}
      {borrowContextPoints.map((partner, idx) => (
        <div
          key={partner.title}
          className="relative h-4 w-4 rounded-full border border-background"
          style={{
            marginLeft: hasHypurrPoints || idx > 0 ? '-4px' : '0',
            zIndex: 9 - idx,
          }}
        >
          <Image
            src={assets[partner.icon as keyof typeof assets]}
            alt={partner.title}
            className="h-full w-full rounded-full"
          />
        </div>
      ))}
    </div>
  ) : null

  return (
    <div
      className={cn(
        'mx-auto w-full max-w-lg rounded-xl border border-white/[0.08] bg-white/[0.02] p-4',
        hasPosition && 'bg-gradient-to-r from-gradient-yellow/5 to-gradient-green/5',
      )}
    >
      <div className="flex w-full flex-col">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative shrink-0">
              <div className="-space-x-2 flex">
                <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-neutral-800 ring-2 ring-background">
                  <TokenImage
                    src={collateralImage}
                    tokenAddress={pair.collateralTokenInfo?.address}
                    alt={pair.collateralSymbol || ''}
                    width={32}
                    height={32}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-neutral-800 ring-2 ring-background">
                  <TokenImage
                    src={liabilityImage}
                    tokenAddress={pair.liabilityTokenInfo?.address}
                    alt={pair.liabilitySymbol || ''}
                    width={32}
                    height={32}
                    className="h-full w-full object-cover"
                  />
                </div>
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="absolute right-0 bottom-0 translate-x-1/4 translate-y-1/4">
                    <TokenImage
                      src={DEFAULT_CURATOR_LOGO}
                      alt={curatorName}
                      width={14}
                      height={14}
                      className="rounded-full bg-card ring-1 ring-background"
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContentShort>{curatorName}</TooltipContentShort>
              </Tooltip>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="flex items-center gap-1.5 font-semibold text-foreground text-sm leading-tight">
                <span>
                  {pair.collateralSymbol} / {pair.liabilitySymbol}
                </span>
                <CardRouteBadge badge={cardRouteBadge} />
              </span>
              <span className="text-dim-foreground text-xs leading-tight">{displayName}</span>
            </div>
          </div>
          <Link
            href={destination}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground transition-opacity hover:opacity-80"
          >
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="mt-2 flex flex-col gap-2 border-white/[0.04] border-t pt-3">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-xs">{showLeverageMetrics ? 'ROE' : 'APY'}</span>
            {showLeverageMetrics ? (
              maxRoe !== undefined ? (
                <span className={cn('font-medium text-sm', isPositiveRoe ? 'text-foreground' : 'text-red-400')}>
                  {isPositiveRoe ? '' : '-'}{Math.abs(maxRoe).toFixed(2)}%
                </span>
              ) : (
                <Dash />
              )
            ) : (
              <span className="font-medium text-foreground text-sm">
                {pair.borrowAPY !== undefined ? `${pair.borrowAPY.toFixed(2)}%` : '0.00%'}
              </span>
            )}
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-xs">LTV</span>
            {maxLTVPercent ? (
              <span className="font-medium text-foreground text-sm">
                {maxLTVPercent}{liqLTVPercent ? ` / ${liqLTVPercent}` : ''}
              </span>
            ) : (
              <Dash />
            )}
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-xs">Liquidity</span>
            {liquidityDisplay === '-' ? <Dash /> : <span className="font-medium text-foreground text-sm">{liquidityDisplay}</span>}
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-xs">Util.</span>
            {pair.utilization !== undefined && !Number.isNaN(pair.utilization) ? (
              <span className="font-medium text-foreground text-sm">{pair.utilization.toFixed(1)}%</span>
            ) : (
              <Dash />
            )}
          </div>
          {(hasPoints || hasParallelIncentive) && (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground text-xs">Points</span>
              <div className="flex items-center gap-1">
                {pointsIcons}
                {hasParallelIncentive && <ParallelIncentiveBadge />}
              </div>
            </div>
          )}
        </div>

        {(hasPosition || (pair.walletBalanceAmount !== undefined && pair.walletBalanceAmount > 0)) && (
          <div className="mt-2 flex flex-col gap-2 border-white/[0.04] border-t pt-3">
            {hasPosition && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground text-xs">Position</span>
                <div className="flex flex-col items-end">
                  {pair.userBorrowAmountUsd !== undefined && pair.userBorrowAmountUsd > 0 ? (
                    <span className="font-medium text-green-400 text-sm">
                      ${pair.userBorrowAmountUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  ) : (
                    <Dash />
                  )}
                  <span className="text-muted-foreground text-xs">
                    {pair.userBorrowAmount?.toFixed(4)} {pair.liabilitySymbol}
                  </span>
                </div>
              </div>
            )}
            {pair.walletBalanceAmount !== undefined && pair.walletBalanceAmount > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground text-xs">In Wallet</span>
                <span className="font-medium text-foreground text-sm">
                  {pair.walletBalanceAmount.toFixed(4)} {pair.collateralSymbol}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export function EBorrowListView({
  pairs,
  showLeverageMetrics = false,
}: EBorrowListViewProps) {
  if (pairs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-16">
        <p className="text-lg text-white/60">No borrow pairs found</p>
        <p className="text-sm text-white/40">Try adjusting your filters</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      {pairs.map((pair) => (
        <BorrowCard
          key={`${pair.collateral}-${pair.liability}`}
          pair={pair}
          showLeverageMetrics={showLeverageMetrics}
        />
      ))}
    </div>
  )
}
